import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Compass, Flame } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { wings, heroImage } from '../mock';

const Home = () => {
  const { theme } = useTheme();

  return (
    <div style={{ background: theme.body, color: theme.bodyText }} className="transition-colors duration-500">
      {/* HERO */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '88vh' }}>
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: `url('${heroImage}')` }}
        />
        <div className="absolute inset-0" style={{ background: theme.overlay }} />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 flex flex-col items-center text-center pb-12 md:pb-16" style={{ minHeight: '92vh', paddingTop: '58vh' }}>
          <h1
            className="font-serif font-bold leading-[1.05] text-4xl md:text-5xl lg:text-6xl"
            style={{ color: theme.heroAccent, textShadow: '0 4px 30px rgba(0,0,0,0.65)' }}
          >
            A Global India Proud of<br />Its Vedic Roots
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg font-light leading-relaxed" style={{ color: '#f4ecd8', textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
            Preserving, Researching, and Propagating Indic Cultures,<br className="hidden md:block" /> Languages, Philosophy & Identity
          </p>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium tracking-wide text-base transition-all duration-300 hover:gap-3 hover:scale-[1.03]"
            style={{ background: theme.heroAccent, color: theme.heroAccentText, boxShadow: '0 10px 30px -8px rgba(0,0,0,0.45)' }}
          >
            Explore Our Mission <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* OUR WINGS */}
      <section className="py-24 px-6 lg:px-10" style={{ background: theme.body }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs tracking-[0.3em] mb-3 font-medium" style={{ color: theme.brand }}>— TRAYI —</div>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold" style={{ color: theme.brand }}>Our Wings</h2>
            <p className="mt-5 max-w-2xl mx-auto text-base md:text-lg" style={{ color: theme.muted }}>
              The Sagi Venkata Narasimham Foundation operates through three specialized wings, each dedicated to advancing Vedic knowledge and heritage.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-7">
            {wings.map((w, i) => {
              const Icon = [BookOpen, Compass, Flame][i % 3];
              return (
                <div
                  key={w.code}
                  className="group relative rounded-2xl p-8 transition-all duration-500 hover:-translate-y-2"
                  style={{
                    background: theme.cardBg,
                    border: `1px solid ${theme.cardBorder}`,
                    boxShadow: '0 20px 50px -25px rgba(0,0,0,0.4)',
                  }}
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-500 group-hover:rotate-6"
                    style={{ background: theme.accent, color: theme.accentText }}
                  >
                    <Icon size={26} />
                  </div>
                  <div className="text-2xl font-serif font-bold mb-3" style={{ color: theme.brand }}>{w.code}</div>
                  <div className="font-serif text-lg leading-snug mb-3" style={{ color: theme.cardText }}>{w.title}</div>
                  <p className="text-sm leading-relaxed mb-6" style={{ color: theme.muted }}>{w.short}</p>
                  <Link
                    to={w.to}
                    className="inline-flex items-center gap-1.5 text-sm font-medium tracking-wide transition-all duration-300 group-hover:gap-3"
                    style={{ color: theme.brand }}
                  >
                    Learn More <ArrowRight size={15} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR MISSION */}
      <section className="py-24 px-6 lg:px-10" style={{ background: theme.sectionAlt }}>
        <div className="max-w-5xl mx-auto text-center">
          <div className="text-xs tracking-[0.3em] mb-3 font-medium" style={{ color: theme.brand }}>— SANKALPA —</div>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-8" style={{ color: theme.brand }}>Our Mission</h2>
          <p className="text-lg md:text-xl leading-relaxed mb-6" style={{ color: theme.bodyText }}>
            To preserve, research, and propagate Indic cultures, languages, philosophy, and identity whose existence are threatened by globalization. Through rigorous scholarship, accessible education, and community engagement, we work to ensure that the profound wisdom of the Vedas and Indic civilization remains vibrant and relevant for future generations.
          </p>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: theme.muted }}>
            Our foundation serves as a beacon of Indic wisdom for the modern world, building harmony and mutual respect in globalization while fostering a Vedic Renaissance for addressing humanity&apos;s contemporary challenges.
          </p>
        </div>
      </section>

      {/* JOIN OUR JOURNEY */}
      <section className="py-24 px-6 lg:px-10" style={{ background: theme.body }}>
        <div
          className="max-w-5xl mx-auto rounded-3xl p-12 md:p-16 text-center relative overflow-hidden"
          style={{
            background: theme.cardBg,
            border: `1px solid ${theme.cardBorder}`,
            boxShadow: '0 30px 60px -30px rgba(0,0,0,0.4)',
          }}
        >
          <div className="absolute -top-10 -right-10 text-9xl font-serif opacity-10 select-none" style={{ color: theme.brand }}>ओं</div>
          <h2 className="font-serif text-3xl md:text-5xl font-semibold mb-5" style={{ color: theme.brand }}>Join Our Journey</h2>
          <p className="max-w-2xl mx-auto mb-8" style={{ color: theme.muted }}>
            Discover the richness of Vedic knowledge and become part of our community dedicated to intellectual and spiritual growth.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium tracking-wide transition-all duration-300 hover:gap-3 hover:scale-[1.03]"
            style={{ background: theme.accent, color: theme.accentText, boxShadow: '0 10px 30px -8px rgba(0,0,0,0.35)' }}
          >
            Learn More About Us <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
